import { TitleButton } from '@/common/buttons/TitleButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityDetail } from '@/common/entityFramework/entityDetail/EntityDetail';
import { EntityForm } from '@/common/entityFramework/entityForm/EntityForm';
import { useEntityMgr } from '@/common/entityFramework/entityView/EntityMgrContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ValList } from '@/common/val/ValList';
import { PriceResFormulaFormField } from '@/md/entities/priceRes/formFields/PriceResFormulaFormField';
import { PriceResInfoDialog } from '@/md/entities/priceRes/PriceResInfoDialog';
import { PriceResVarsList } from '@/md/entities/priceRes/PriceResVarsList';
import { PCT_CALC_TYPES } from '@/md/entities/progProd/progProdCalcTypeUtils';
import { ProgProdSaleLineList } from '@/sc/entities/dirSaleLine/ProgProdSaleLineList';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useLayoutEffect } from 'react';

export const PriceResDetail = () => {
  const { entityObj, setEntityObj, entityDef } = useEntityMgr();
  const rsrc = useRsrc();
  const { openDialog } = useDialog();

  // Stamp a clean placeholder before paint so the raw Entity code never flashes on screen
  useLayoutEffect(() => {
    if (entityObj.id && !entityObj.name) {
      setEntityObj({ ...entityObj, name: entityDef.displayName });
    }
  }, [entityObj, setEntityObj, entityDef]);

  // PriceRes has no name/code of its own, so derive a readable title from its Product
  useEffect(() => {
    if (!entityObj.prodId) return;

    rsrc.byId('md', 'PROD', entityObj.prodId).then((prodObj) => {
      const computedName = prodObj?.name && prodObj.name + ' Price Results';
      if (computedName && entityObj.name !== computedName) {
        setEntityObj({ ...entityObj, name: computedName });
      }
    });
  }, [entityObj, setEntityObj, rsrc]);

  const augButtons = (buttons) => {
    buttons.push(
      <TitleButton
        key="priceResInfoButton"
        icon={fas.faInfoCircle}
        toolTip="Formula"
        onClick={() =>
          openDialog(PriceResInfoDialog, {
            data: { formula: entityObj.tierFormula, vars: entityObj.tierVars },
          })
        }
      />,
    );
  };

  const augFieldDefs = (fieldToFieldDef) => {
    fieldToFieldDef.tierFormula.displayName = 'Formula';
    fieldToFieldDef.tierFormula.component = <PriceResFormulaFormField fieldDef={fieldToFieldDef.tierFormula} />;

    fieldToFieldDef.tierVars.hide = true;

    // Concession holds a percent, a contract price or a per-unit amount depending on the Calculation Type
    if (PCT_CALC_TYPES.includes(entityObj.progProdCalcType)) {
      fieldToFieldDef.conc.type = 'PERCENT';
      fieldToFieldDef.conc.decimalPrecision = 2;
    }
  };

  const tabSpecs = [
    { id: 'infoTab', label: 'Info', content: <EntityForm augFieldDefs={augFieldDefs} /> },
    {
      id: 'saleLine',
      label: 'Sale Lines',
      content: (
        <ProgProdSaleLineList
          progProdId={entityObj.progProdId}
          custId={entityObj.custId}
          startDate={entityObj.startDate}
          endDate={entityObj.endDate}
        />
      ),
    },
    {
      id: 'varsTab',
      label: 'Variables',
      content: <PriceResVarsList />,
    },
    { id: 'val', label: 'Validations', content: <ValList parentEntity="PRICE_RES" parentEntityId={entityObj.id} /> },
  ];

  return <EntityDetail tabSpecs={tabSpecs} augButtons={augButtons} />;
};
